import { useQueries } from '@tanstack/react-query'
import { FilterBody, FilterQuery } from '@yes.technology/react-toolkit'
import { merge } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import deepOmit from 'shared/utils/object/deepOmit'
import fetchYesObjects from 'yesObject/shared/api/fetchYesObjects'
import { Operator } from '../../FilterFields/types'
import { baseProps } from '../contants'
import getClonedFilterquery from '../utils/getClonedFilterquery'
import getDisabledArrow from '../utils/getDisabledArrow'
import getFilteredStartDate from '../utils/getFilteredStartDate'
import getOperatorThreshold from '../utils/getOperatorThreshold'

type UseMonthsNavigationProps = {
  startDate: string
  filterquery: FilterQuery
  filterFieldsFilterQuery: FilterQuery
  objectclassUuid: string
}

type GetFilterBodyFromMonthsWithEventsProps = {
  filterquery: FilterQuery
  filterFieldsFilterQuery?: FilterQuery
  objectclassUuid: string
  dateString: string
}

const getReferenceDate = (startDate?: string, currentDate?: string) => {
  if (currentDate) {
    return currentDate
  }

  if (startDate) {
    return startDate
  }

  return null
}

const getFilterBodyFromMonthsWithEvents = ({
  filterquery,
  filterFieldsFilterQuery,
  objectclassUuid,
  dateString
}: GetFilterBodyFromMonthsWithEventsProps): FilterBody[] => {
  if (!dateString) {
    return []
  }

  const date = new Date(dateString)

  const endOfLastMonthDate = new Date(date.getFullYear(), date.getMonth(), 0)
  endOfLastMonthDate.setHours(23, 59, 59, 999)
  endOfLastMonthDate.setHours(0, 0, 0, 0)

  const endOfMonthDate = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  const startOfNextMonthDate = new Date(endOfMonthDate)
  startOfNextMonthDate.setDate(endOfMonthDate.getDate() + 1)
  startOfNextMonthDate.setHours(0, 0, 0, 0)

  const endOfLastMonth = endOfLastMonthDate.toISOString()

  const startOfNextMonth = startOfNextMonthDate.toISOString()

  const { filteredStartDate, operator } = getFilteredStartDate(
    filterFieldsFilterQuery
  )

  const { hasLowerThresholdFilter, hasUpperThresholdFilter } =
    getOperatorThreshold(operator as Operator)

  return [
    {
      ...baseProps,
      objectclass: objectclassUuid,
      orderby: [
        {
          start_date: 'desc'
        }
      ],
      query: {
        ...merge(
          getClonedFilterquery(filterquery),
          deepOmit(filterFieldsFilterQuery ?? {}, 'start_date.des'),
          {
            $and: [
              {
                'start_date.des': {
                  $lte: endOfLastMonth,
                  ...(hasLowerThresholdFilter ? { $gt: filteredStartDate } : {})
                }
              }
            ]
          }
        )
      }
    },
    {
      ...baseProps,
      objectclass: objectclassUuid,
      orderby: [
        {
          start_date: 'asc'
        }
      ],
      query: {
        ...merge(
          getClonedFilterquery(filterquery),
          deepOmit(filterFieldsFilterQuery ?? {}, 'start_date.des'),
          {
            $and: [
              {
                'start_date.des': {
                  $gte: startOfNextMonth,
                  ...(hasUpperThresholdFilter ? { $lt: filteredStartDate } : {})
                }
              }
            ]
          }
        )
      }
    }
  ]
}

const getNavigationTitle = (stringDate: string, language: AllowedLocales) => {
  const date = new Date(stringDate)
  const month = date.toLocaleString(language, { month: 'long' })
  const year = date.getFullYear()

  return `${month.charAt(0).toUpperCase()}${month.slice(1)}/${year}`
}

const useMonthsNavigation = ({
  startDate,
  filterquery,
  filterFieldsFilterQuery,
  objectclassUuid
}: UseMonthsNavigationProps) => {
  const { language, t } = useTranslation()

  const [currentStartDate, setCurrentStartDate] = useState<string>('')

  const date = getReferenceDate(startDate, currentStartDate)

  useEffect(() => {
    if (!filterFieldsFilterQuery) {
      setCurrentStartDate('')
    }
  }, [filterFieldsFilterQuery])

  useEffect(() => {
    if (!startDate) return

    setCurrentStartDate(startDate)
  }, [startDate])

  const queries = getFilterBodyFromMonthsWithEvents({
    filterquery,
    filterFieldsFilterQuery,
    objectclassUuid,
    dateString: date || ''
  })

  const results = useQueries({
    queries: queries.map((filterBody) => ({
      queryKey: [
        'months-with-available-events',
        filterBody.objectclass,
        filterBody.query
      ],
      queryFn: () => fetchYesObjects({ filterBody, showLoading: false }),
      enabled: !!filterBody && !!objectclassUuid
    })),
    combine: (results) => {
      const [previousMonthResult, nextMonthResult] = results

      const previousMonth = previousMonthResult?.data?.objects?.[0]?.start_date
      const nextMonth = nextMonthResult?.data?.objects?.[0]?.start_date

      const isLoading = results.some((result) => result.isLoading)

      return {
        isLoading,
        navigation: {
          title: date
            ? getNavigationTitle(date as string, language as AllowedLocales)
            : t('system.loading'),
          date: date || '',
          disabled: !nextMonth && !previousMonth,
          disabledArrow: getDisabledArrow(previousMonth, nextMonth),
          onNextClick: () => {
            if (!nextMonth) return

            setCurrentStartDate(nextMonth as string)
          },
          onPrevClick: () => {
            if (!previousMonth) return

            setCurrentStartDate(previousMonth as string)
          },
          textVariant: 'content-emphasis-06' as const
        }
      }
    }
  })

  return results
}

export default useMonthsNavigation
